<template>
  <v-app>
    <!-- Top Menu Bar -->
    <v-app-bar
      flat
      app
      :color="barBg"
      height="60"
      :style="{ 'margin-left': $vuetify.breakpoint.mdAndDown ? '0' : '8px' }"
    >
      <!-- icon for mobile nav drawer -->
      <v-app-bar-nav-icon v-if="$vuetify.breakpoint.mdAndDown" @click.stop="isDrawerOpen = !isDrawerOpen">
        <v-icon :color="barTheme" class="fi fi-rr-arrow-alt-square-right" style="font-size: 22px"></v-icon>
      </v-app-bar-nav-icon>

      <!-- Left logo -->
      <div v-if="logo_img">
        <v-img :src="`${$mediaPath}${logo_img}`" :max-height="60" :max-width="200" contain position="left"></v-img>
      </div>

      <v-spacer />

      <!-- for desktop app bar -->
      <v-menu
        flat
        bottom
        offset-y
        nudge-bottom="10"
        rounded="lg"
        content-class="elevation-0 elevation-custom"
        transition="slide-y-transition"
        v-model="isUserMenuOpen"
        :close-on-content-click="!disableCloseMenu"
      >
        <!-- User Display Button -->
        <template #activator="{ on, attrs }">
          <v-btn v-bind="attrs" v-on="on" depressed text :class="`user-menu-open-button ${barTheme}--text`">
            <span>{{ userName }}{{ $validate.DataValid(schoolName) ? ' | ' : '' }}{{ schoolName }}</span>
            <v-icon
              class="ml-8 fi"
              :class="{
                'fi-ss-angle-small-down': !isUserMenuOpen,
                'fi-ss-angle-small-up': isUserMenuOpen,
              }"
              size="20"
            ></v-icon>
          </v-btn>
        </template>

        <v-list class="px-2" dense nav>
          <!-- 切換分校 -->
          <NavListItem
            v-if="userType !== 'student'"
            class="user-menu-item logout-btn"
            icon="fi fi-rr-replace"
            @btnClick="handleUpdateSelectedSchool()"
            :name="$t('switchCampus')"
          />

          <!-- 語言 -->
          <NavListItem
            class="user-menu-item logout-btn"
            icon="fi fi-rr-language"
            @btnClick="handleSelectedLang()"
            :name="$t('language')"
          />

          <!-- 登出 -->
          <NavListItem
            class="user-menu-item logout-btn"
            icon="fi fi-rr-sign-in-alt"
            @btnClick="logoutAndCloseMenu()"
            :name="$t('logout')"
          />
        </v-list>
      </v-menu>
    </v-app-bar>

    <!-- Sidebar -->
    <NavDrawer :drawerItemList="drawerItems" :isDrawerOpen.sync="isDrawerOpen"></NavDrawer>

    <!-- Modal: 切換分校 -->
    <FormDialog
      title="selectCampus"
      :maxWidth="500"
      ref="selectCampusDialog"
      formRef="selectCampusForm"
      btnAlign="center"
    >
      <v-radio-group v-model="tempSelectedSchoolId">
        <v-radio
          v-for="item in tutorSchoolOptions"
          :key="item.value"
          :label="item.text"
          :value="item.value"
          hide-details
        ></v-radio>
      </v-radio-group>
    </FormDialog>

    <!-- Modal: 語言 -->
    <FormDialog title="chooseLanguage" :maxWidth="500" ref="langDialog" formRef="langForm" btnAlign="center">
      <v-radio-group v-model="selectedLang">
        <v-radio
          v-for="item in langOptions"
          :key="item.value"
          :label="item.text"
          :value="item.value"
          hide-details
        ></v-radio>
      </v-radio-group>
    </FormDialog>

    <!-- Main Content -->
    <v-main>
      <div :class="mainContentClass">
        <v-fade-transition mode="out-in">
          <div>
            <template>
              <slot> </slot>
            </template>
          </div>
        </v-fade-transition>
      </div>
    </v-main>
  </v-app>
</template>

<script>
import NavListItem from '@/components/NavDrawer/NavListItem.vue'
import NavDrawer from '@/components/NavDrawer/NavDrawer.vue'
import FormDialog from '@/components/FormDialog.vue'

export default {
  components: {
    NavListItem,
    NavDrawer,
    FormDialog,
  },
  data: () => ({
    isDrawerOpen: false,
    isUserMenuOpen: false,
    disableCloseMenu: false,

    drawerItems: [],

    userId: null,
    userName: '',
    userType: '',

    tutorSchoolOptions: [],
    schoolName: '',
    tempSelectedSchoolId: null,

    selectedLang: 'zh',
    langOptions: [
      { value: 'zh', text: '繁體中文' },
      { value: 'en', text: 'English' },
    ],

    barBg: '#ffffff',
    barTheme: 'dark',
    logo_img: '',
  }),
  computed: {
    mainContentClass() {
      const fullPages = ['PayTuitionPayment', 'OrderList', 'StudentUserOrderList']
      return {
        'fullW-container pt-5 pb-2 px-2': fullPages.includes(this.$route.name),
        'app-content-container boxed-container py-6 px-3 px-sm-6 px-md-6 px-lg-6 px-xl-6': !fullPages.includes(
          this.$route.name,
        ),
      }
    },
    getFullPath() {
      return this.$route.path
    },
  },
  watch: {
    getFullPath() {
      this.initPage()
    },
  },
  methods: {
    initDrawerItems() {
      let menus = [
        {
          isGroup: this.isAppMode(['T01']) ? false : true,
          id: 'pay-tuition',
          text: this.$t('menu.payTuition'),
          linkName: 'PayTuitionList',
          icon: 'fi fi-rr-piggy-bank',
          items: [
            {
              text: this.$t('menu.payTuition'),
              linkName: 'PayTuitionList',
              subPages: ['PayTuitionPayment'],
            },
            {
              text: this.$t('menu.payTuitionUnpaid'),
              linkName: 'PayTuitionListWithStatus',
              subPages: ['PayTuitionPayment'],
            },
          ],
        },
        {
          isGroup: false,
          id: 'requirement',
          text: this.$t('menu.requirement'),
          linkName: 'SwitchRequirement',
          icon: 'fi fi-rr-replace',
        },
        {
          isGroup: false,
          id: 'timetable',
          text: this.$t('menu.timetable'),
          linkName: 'Timetable',
          icon: 'fi fi-rr-calendar',
          subPages: ['CreateClass', 'ClassDetails'],
        },
        {
          isGroup: false,
          id: 'tutor-user-leave',
          text: this.$t('menu.tutorLeave'),
          linkName: 'TutorUserLeave',
          icon: 'fi fi-rr-calendar-day',
        },
        {
          isGroup: false,
          id: 'student',
          text: this.$t('menu.student'),
          linkName: 'StudentList',
          icon: 'fi fi-rr-grin',
          subPages: ['StudentDetails', 'CreateStudent', 'StudentAttendance', 'StudentPoints'],
        },
        {
          isGroup: false,
          id: 'student-user',
          text: this.$t('menu.studentInfo'),
          linkName: 'StudentUserDetails',
          icon: 'fi fi-rr-grin',
        },
        {
          isGroup: false,
          id: 'student-requirement',
          text: this.$t('menu.requirement'),
          linkName: 'StudentSwitchRequirement',
          icon: 'fi fi-rr-replace',
        },
        {
          isGroup: false,
          id: 'student-user-attendance',
          text: this.$t('attendanceRecord'),
          linkName: 'StudentUserAttendance',
          icon: 'fi fi-rr-memo',
        },
        {
          isGroup: false,
          id: 'student-user-order',
          text: this.$t('menu.order'),
          linkName: 'StudentUserOrderList',
          icon: 'fi fi-rr-donate',
        },
        {
          isGroup: true,
          id: 'course',
          text: this.$t('menu.course'),
          icon: 'fi fi-rr-graduation-cap',
          items: [
            {
              id: 'course-category',
              text: this.$t('menu.courseCategory'),
              linkName: 'CourseCategoryList',
              subPages: ['CourseCategoryDetails', 'CreateCourseCategory'],
            },
            {
              id: 'course-list',
              text: this.$t('menu.allCourses'),
              linkName: 'CourseList',
              subPages: ['CourseDetails', 'CreateCourse'],
            },
          ],
        },
        {
          isGroup: false,
          id: 'grade',
          text: this.$t('menu.grade'),
          linkName: 'GradeSetting',
          icon: 'fi fi-rr-graduation-cap',
        },
        {
          isGroup: true,
          id: 'online-course',
          text: this.$t('menu.onlineCourse'),
          icon: 'fi fi-rr-graduation-cap',
          items: [
            {
              id: 'online-course-category',
              text: this.$t('menu.onlineCourseCategory'),
              linkName: 'OnlineCourseCategoryList',
              subPages: ['OnlineCourseCategoryDetails', 'CreateOnlineCourseCategory'],
            },
            {
              id: 'online-course-list',
              text: this.$t('menu.allOnlineCourse'),
              linkName: 'OnlineCourseList',
              subPages: ['OnlineCourseDetails', 'CreateOnlineCourse'],
            },
          ],
        },
        {
          isGroup: false,
          id: 'order',
          text: this.$t('menu.order'),
          linkName: 'OrderList',
          icon: 'fi fi-rr-donate',
        },
        {
          isGroup: true,
          id: 'product',
          text: this.$t('menu.product'),
          icon: 'fi fi-rr-cube',
          items: [
            {
              id: 'product-list',
              text: this.$t('menu.allProducts'),
              linkName: 'ProductList',
              subPages: ['ProductDetails', 'CreateProduct'],
            },
            {
              id: 'product-category',
              text: this.$t('menu.productCategory'),
              linkName: 'ProductCategoryList',
              subPages: ['ProductCategoryDetails', 'CreateProductCategory'],
            },
          ],
        },
        {
          isGroup: false,
          id: 'package',
          text: this.$t('menu.package'),
          linkName: 'PackageList',
          icon: 'fi fi-rr-ticket',
          subPages: ['PackageDetails', 'CreatePackage'],
        },
        {
          isGroup: false,
          id: 'month-pass',
          text: this.$t('menu.monthPass'),
          linkName: 'MonthPassList',
          icon: 'fi fi-rr-ticket',
          subPages: ['MonthPassDetails', 'CreateMonthPass'],
        },
        {
          isGroup: false,
          id: 'school',
          text: this.$t('menu.campus'),
          linkName: 'SchoolList',
          icon: 'fi fi-rr-book-open-cover',
          subPages: ['SchoolDetails', 'CreateSchool'],
        },
        {
          isGroup: false,
          id: 'classroom',
          text: this.$t('menu.classroom'),
          linkName: 'ClassroomList',
          icon: 'fi fi-rr-door-open',
          subPages: ['ClassroomDetails', 'CreateClassroom'],
        },
        {
          isGroup: false,
          id: 'tutor',
          text: this.$t('menu.tutor'),
          linkName: 'TutorList',
          icon: 'fi fi-rr-chalkboard-user',
          subPages: ['TutorDetails', 'CreateTutor'],
          items: [
            {
              id: 'tutor-list',
              text: this.$t('menu.allTutors'),
              linkName: 'TutorList',
              subPages: ['TutorDetails', 'CreateTutor'],
            },
            {
              id: 'tutor-salary',
              text: this.$t('menu.salaryReport'),
              linkName: 'SalaryReport',
            },
            {
              id: 'tutor-calendar',
              text: this.$t('menu.tutorCalendar'),
              linkName: 'TutorCalendar',
            },
            {
              id: 'tutor-leave',
              text: this.$t('menu.tutorLeave'),
              linkName: 'TutorLeave',
            },
          ],
        },
        {
          isGroup: false,
          id: 'coupon',
          text: this.$t('menu.coupon'),
          linkName: 'CouponList',
          icon: 'fi fi-rr-gift',
          subPages: ['CouponDetails', 'CreateCoupon'],
        },
        {
          isGroup: false,
          id: 'notify',
          text: this.$t('menu.notification'),
          linkName: 'NotifyList',
          icon: 'fi fi-rr-bell',
          subPages: ['NotifyDetails', 'CreateNotify'],
        },
        {
          isGroup: true,
          id: 'other',
          text: this.$t('menu.other'),
          icon: 'fi fi-rr-blog-pencil',
          items: [
            {
              isGroup: true,
              id: 'blog',
              text: this.$t('menu.blog'),
              items: [
                {
                  id: 'blog-category',
                  text: this.$t('menu.blogCategory'),
                  linkName: 'BlogCategoryList',
                  subPages: ['BlogCategoryDetails', 'CreateBlogCategory'],
                },
                {
                  id: 'blog-list',
                  text: this.$t('menu.allBlogs'),
                  linkName: 'BlogList',
                  subPages: ['BlogDetails', 'CreateBlog'],
                },
              ],
            },
            {
              isGroup: true,
              id: 'video',
              text: this.$t('menu.tutorialVideo'),
              items: [
                {
                  id: 'video-category',
                  text: this.$t('menu.videoCategory'),
                  linkName: 'VideoCategoryList',
                  subPages: ['VideoCategoryDetails', 'CreateVideoCategory'],
                },
                {
                  id: 'video-list',
                  text: this.$t('menu.allVideos'),
                  linkName: 'VideoList',
                  subPages: ['VideoDetails', 'CreateVideo'],
                },
              ],
            },
            {
              isGroup: true,
              id: 'website-setting',
              text: this.$t('menu.websiteSetting'),
              items: [
                {
                  id: 'website-setting-home',
                  text: this.$t('menu.homeDesign'),
                  linkName: 'WebsiteHomeSetting',
                },
                {
                  id: 'website-setting-payment',
                  text: this.$t('menu.paymentSetting'),
                  linkName: 'WebsitePaymentSetting',
                },
              ],
            },
            {
              isGroup: false,
              id: 'about-us',
              text: this.$t('menu.aboutUs'),
              linkName: 'AboutUs',
            },
            {
              isGroup: false,
              id: 'useful-links',
              text: this.$t('menu.usefulLinks'),
              linkName: 'UsefulLinks',
            },
            {
              isGroup: false,
              id: 'festival-promotions',
              text: this.$t('menu.festivalPromotions'),
              linkName: 'FestivalPromotions',
            },
            {
              isGroup: false,
              id: 'faq',
              text: this.$t('menu.faq'),
              linkName: 'Faq',
            },
            {
              isGroup: false,
              id: 'collaborations',
              text: this.$t('menu.collaborations'),
              linkName: 'Collaborations',
            },
          ],
        },
        {
          isGroup: false,
          id: 'student-blog',
          text: this.$t('menu.blog'),
          linkName: 'StudentBlogList',
          icon: 'fi fi-rr-blog-pencil',
          subPages: ['StudentBlogDetails'],
        },
        {
          isGroup: true,
          id: 'setting',
          text: this.$t('menu.setting'),
          icon: 'fi fi-rr-settings',
          show: true,
          items: [
            {
              id: 'setting-basic',
              text: this.$t('menu.basicSetting'),
              linkName: 'BasicSetting',
            },
            {
              id: 'setting-payment',
              text: this.$t('menu.paymentSetting'),
              linkName: 'PaymentSetting',
            },
            {
              id: 'setting-points',
              text: this.$t('menu.pointsSetting'),
              linkName: 'PointsSetting',
            },
            {
              id: 'setting-holiday',
              text: this.$t('menu.holidaySetting'),
              linkName: 'HolidaySetting',
            },
            {
              id: 'setting-requirement',
              text: this.$t('menu.switchSetting'),
              linkName: 'SwitchSetting',
            },
          ],
        },
        {
          isGroup: false,
          id: 'report',
          text: this.$t('menu.report'),
          linkName: 'Report',
          icon: 'fi fi-rr-chart-histogram',
        },
        {
          isGroup: false,
          id: 'enquiry',
          text: this.$t('menu.enquiry'),
          linkName: 'Enquiry',
          icon: 'fi fi-rr-headset',
        },
      ]

      const tutorIndex = menus.findIndex(el => el.id === 'tutor')
      const tutorMenus = this.handleSubList(JSON.parse(JSON.stringify(menus[tutorIndex].items)))
      if (tutorMenus.length !== 1 || (tutorMenus.length === 1 && tutorMenus[0].id !== 'tutor-list')) {
        menus[tutorIndex].isGroup = true
        menus[tutorIndex].subPages = []
      }

      this.drawerItems = this.handleSubList(menus)
    },

    handleSubList(_list = []) {
      let list = JSON.parse(JSON.stringify(_list))
      const routes = this.$router.getRoutes()

      for (let i = list.length - 1; i >= 0; i--) {
        let show = true

        // filter out unable route
        if (list[i].isGroup) {
          list[i].items = this.handleSubList(list[i].items || [])
          if (!this.$validate.DataValid(list[i].items) || list[i].items.length < 1) {
            show = false
          }
        } else {
          const foundRoute = routes.find(el => el.name === list[i].linkName)
          if (
            !foundRoute ||
            (this.$validate.DataValid(foundRoute.meta) &&
              this.$validate.DataValid(foundRoute.meta.cannotAccess, true) &&
              foundRoute.meta.cannotAccess.includes(this.userType))
          ) {
            show = false
          }
        }

        if (!show) {
          list.splice(i, 1)
        } else {
          list[i].show = true
        }
      }

      return list
    },

    async getSetting() {
      const localLogo = this.gfGetSettingLogo()
      if (localLogo !== null) {
        this.logo_img = localLogo
      } else {
        try {
          if (!this.gDisableSettingByCenter) {
            const selectedSchool = this.gfGetUserSelectedSchool()
            if (selectedSchool > -1) {
              const data = await this.$Fetcher.GetSchoolById(selectedSchool)
              if (!data.basic_use_share) {
                this.logo_img = data['logo'] || ''
                this.gfUpdateSettingLogo(this.logo_img)
                return
              }
            }
          }
        } catch (err) {
          this.$common.error(err)
        }

        try {
          const data = await this.$Fetcher.GetSetting()
          this.logo_img = data.logo || ''
          this.gfUpdateSettingLogo(this.logo_img)
        } catch (err) {
          this.$common.error(err)
        }
      }
    },

    async getGradeOptions() {
      let grades = []
      if (this.gEnableCustomGrade) {
        try {
          const data = await this.$Fetcher.GetGrades()
          grades = data.map(el => {
            return { value: el.grade_key, text: el.name }
          })
        } catch {}
      } else {
        grades = this.$gradeOptions
      }
      this.$store.dispatch('setGradeOptions', grades)
    },

    // =============== 登出 ================
    logoutAndCloseMenu() {
      this.disableCloseMenu = false
      this.gfLogout()
    },

    // ============== 切換分校 ==============
    async getSchoolData() {
      const localOptions = this.gfGetSettingSchoolOptions()
      if (localOptions !== null) {
        this.tutorSchoolOptions = localOptions
      } else {
        try {
          const { data } = await this.$Fetcher.GetSchools()
          const filtered = data.filter(el => el.active === true)
          if (this.$validate.DataValid(filtered, true)) {
            this.tutorSchoolOptions = filtered.map(el => {
              return { value: el.id, text: el.name }
            })
          }
          this.gfUpdateSettingSchoolOptions(this.tutorSchoolOptions)
        } catch (err) {
          this.$common.error(err)
        }
      }

      if (this.tutorSchoolOptions.length > 0) {
        this.getUserSchoolsList()
      } else {
        this.dispatchSiteLoaded()
      }
    },
    async getUserSchoolsList() {
      this.tempSelectedSchoolId = null
      if (this.$validate.DataValid(this.userId)) {
        try {
          if (this.userType === 'student') {
            const data = await this.$Fetcher.GetStudentById(this.userId)
            if (data.login_block === true) {
              throw 'message.loginExpired'
            }

            // update username if changed
            if (data.username !== this.userName) {
              await this.gfUpdateUsername(data.username)
              this.userName = data.username
            }
          } else {
            const data = await this.$Fetcher.GetStaffById(this.userId)
            if (data.login_block === true) {
              throw 'message.loginExpired'
            }

            // update user type if changed
            if (data.user_type !== this.userType) {
              await this.gfUpdateUserType(data.user_type)
              this.userType = data.user_type
            }
            // update username if changed
            if (data.username !== this.userName) {
              await this.gfUpdateUsername(data.username)
              this.userName = data.username
            }

            // update center
            const allSchoolIds = this.tutorSchoolOptions.map(el => el.value)
            let userSchoolIds = []
            let userSelectedSchoolId = null
            if (data.user_type === 'developer') {
              userSchoolIds = [...allSchoolIds]
            } else if (this.$validate.DataValid(data.schools)) {
              if (data.schools.includes(-1)) {
                userSchoolIds = [...allSchoolIds]
              } else {
                userSchoolIds = [...data.schools]
                userSchoolIds = userSchoolIds.filter(el => allSchoolIds.includes(el))
              }
            }

            const selectedSchoolId = this.gfGetUserSelectedSchool()
            if (selectedSchoolId > -1 && userSchoolIds.includes(selectedSchoolId)) {
              userSelectedSchoolId = selectedSchoolId
            } else {
              userSelectedSchoolId = userSchoolIds[0]
            }

            await this.gfUpdateUserSchools(userSchoolIds)
            await this.gfUpdateUserSelectedSchool(userSelectedSchoolId)

            this.tutorSchoolOptions = this.tutorSchoolOptions.filter(el => userSchoolIds.includes(el.value))
            this.tempSelectedSchoolId = userSelectedSchoolId
            this.schoolName = ''
            const target = this.tutorSchoolOptions.find(item => item.value === userSelectedSchoolId)
            if (target) this.schoolName = target.text
          }
        } catch (err) {
          this.$common.error(err)
          if (err !== 'abort') {
            this.logoutAndCloseMenu()
          }
        } finally {
          this.dispatchSiteLoaded()
        }
      }
    },
    async handleUpdateSelectedSchool(newOpen = true) {
      if (newOpen) {
        const selectedSchoolId = this.gfGetUserSelectedSchool()
        this.tempSelectedSchoolId = selectedSchoolId
      } else {
        this.$refs.selectCampusDialog.setLoading(false)
      }

      const confirm = await this.$refs.selectCampusDialog.show()
      if (confirm) {
        if (!this.$validate.DataValid(this.tempSelectedSchoolId) || this.tempSelectedSchoolId < 0) {
          this.$store.dispatch('toggleSnack', { message: 'required' })
          this.handleUpdateSelectedSchool(false)
          return
        }

        let centerData = null
        try {
          centerData = await this.$Fetcher.GetSchoolById(this.tempSelectedSchoolId)
        } catch {
          this.showAlert({
            title: 'message.updateFail',
            message: 'message.centerNotFound',
            isError: true,
          })
          this.handleUpdateSelectedSchool(false)
          return
        }

        const selectedSchoolId = this.gfGetUserSelectedSchool()
        if (this.tempSelectedSchoolId === selectedSchoolId) {
          this.$refs.selectCampusDialog.hide()
          return
        }

        const updated = await this.gfUpdateUserSelectedSchool(this.tempSelectedSchoolId)
        if (updated) {
          let nextName = ''
          for (let i = 0; i < this.drawerItems.length; i++) {
            if (this.drawerItems[i].isGroup && this.$validate.DataValid(this.drawerItems[i].items)) {
              for (let j = 0; j < this.drawerItems[i].items.length; j++) {
                if (
                  this.drawerItems[i].items[j].subPages &&
                  this.drawerItems[i].items[j].subPages.includes(this.$route.name)
                ) {
                  nextName = this.drawerItems[i].items[j].linkName
                  break
                }
              }
            } else if (this.drawerItems[i].subPages && this.drawerItems[i].subPages.includes(this.$route.name)) {
              nextName = this.drawerItems[i].linkName
            }

            if (nextName !== '') {
              break
            }
          }

          this.gfExpiredSettingLogo();

          if (nextName !== '') {
            this.$router.push({ name: nextName })
          } else {
            this.$router.replace({ query: {} })
            this.$router.go()
          }

          this.$refs.selectCampusDialog.hide()
        } else {
          this.$store.dispatch('toggleSnack', {
            message: 'message.updateFail',
            type: 'error',
          })
          this.handleUpdateSelectedSchool(false)
        }
      }
    },
    // ================ 語言 ================
    async handleSelectedLang(newOpen = true) {
      if (newOpen) {
        this.selectedLang = this.$i18n.locale
      } else {
        this.$refs.langDialog.setLoading(false)
      }

      const confirm = await this.$refs.langDialog.show()
      if (confirm) {
        if (!this.$validate.DataValid(this.selectedLang) || this.selectedLang < 0) {
          this.$store.dispatch('toggleSnack', { message: 'required' })
          this.handleSelectedLang(false)
          return
        }

        if (this.$i18n.locale === this.selectedLang) {
          this.$refs.langDialog.hide()
          return
        }

        localStorage.setItem(`${this.$config.VUE_APP_RUN_MODE}_CMS_LANG`, this.selectedLang)
        this.$i18n.locale = this.selectedLang
        this.$refs.langDialog.hide()
        window.scrollTo(0, 0)
        this.$router.go()
      }
    },

    async dispatchSiteLoaded() {
      await this.getGradeOptions()
      const loaded = new Event('onSiteLoaded')
      window.dispatchEvent(loaded)

      this.getSetting()
    },
    async initPage() {
      const check = await this.gfCheckUserData()
      if (check) {
        this.userType = await this.gfGetUserType()
        this.initDrawerItems()
        this.getSchoolData()
      }
    },
  },
  mounted() {
    const userData = this.gfGetCurrentUserData()
    if (this.$validate.DataValid(userData)) {
      this.userName = userData.username
      this.userId = userData.id
    }
  },
  created() {
    if (this.isAppMode(['T01'])) {
      this.barBg = '#1866B5'
      this.barTheme = 'light'
    }

    const lang = localStorage.getItem(`${this.$config.VUE_APP_RUN_MODE}_CMS_LANG`)
    if (lang) {
      this.selectedLang = lang
    }

    this.initPage()
  },
}
</script>

<style lang="scss" scoped>
.boxed-container {
  max-width: 1450px;
  margin-left: auto;
  margin-right: auto;
}

.fullW-container {
  max-width: calc(100% - 60px);
  margin-left: auto;
  margin-right: auto;
}

@media #{map-get($display-breakpoints, 'md-and-down')} {
  .fullW-container {
    max-width: calc(100% - 20px);
  }
}
</style>
